import { useState, useEffect } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { useSiteSettings, type SiteSettings } from '@/lib/siteSettings';

const sectionIcon = (icon: string) => (
  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
    <i className={`${icon} text-base`} />
  </div>
);

export default function AdminSettingsPage() {
  const { settings, loading, updateSettings } = useSiteSettings();

  const [form, setForm] = useState<SiteSettings>({ ...settings });
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'branding' | 'hero' | 'contact' | 'social'>('branding');

  useEffect(() => {
    if (!loading) {
      setForm({ ...settings });
    }
  }, [settings, loading]);

  const handleChange = (key: keyof SiteSettings, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);

    const { error } = await updateSettings(form);
    if (error) {
      setSaveMessage({ type: 'error', text: error });
    } else {
      setSaveMessage({ type: 'success', text: 'Settings saved successfully!' });
      setTimeout(() => setSaveMessage(null), 3000);
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const tabs = [
    { key: 'branding' as const, label: 'Branding', icon: 'ri-palette-line' },
    { key: 'hero' as const, label: 'Hero Section', icon: 'ri-image-line' },
    { key: 'contact' as const, label: 'Contact Info', icon: 'ri-contacts-line' },
    { key: 'social' as const, label: 'Social Media', icon: 'ri-share-line' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground-900">Site Settings</h1>
            <p className="text-sm text-foreground-500 mt-1">Manage your website branding, images, and contact details.</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-background-50 border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <i className="ri-save-line" />
                Save Changes
              </>
            )}
          </button>
        </div>

        {saveMessage && (
          <div className={`px-4 py-3 rounded-lg text-sm font-medium ${
            saveMessage.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {saveMessage.type === 'success' ? (
              <span className="flex items-center gap-2"><i className="ri-check-line" /> {saveMessage.text}</span>
            ) : (
              <span className="flex items-center gap-2"><i className="ri-error-warning-line" /> {saveMessage.text}</span>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-1 p-1 bg-background-100 rounded-full w-fit">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
                activeTab === tab.key
                  ? 'bg-background-50 text-foreground-900 shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bg-background-50 border border-background-200 rounded-xl p-6 md:p-8">
          {/* Branding Tab */}
          {activeTab === 'branding' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                {sectionIcon('ri-palette-line')}
                <div>
                  <h2 className="text-base font-heading font-semibold text-foreground-900">Branding</h2>
                  <p className="text-xs text-foreground-500">Logo and company identity settings</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-2">Logo Image URL</label>
                <input
                  type="text"
                  value={form.logo_url}
                  onChange={(e) => handleChange('logo_url', e.target.value)}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-colors font-mono"
                  placeholder="https://..."
                />
                {form.logo_url && (
                  <div className="mt-3 p-4 bg-background-100 rounded-lg flex items-center justify-center">
                    <img
                      src={form.logo_url}
                      alt="Logo preview"
                      className="h-12 w-auto object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-2">Footer Description</label>
                <textarea
                  value={form.footer_description}
                  onChange={(e) => handleChange('footer_description', e.target.value)}
                  maxLength={500}
                  rows={3}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-colors resize-none"
                  placeholder="Brief company description for the footer..."
                />
                <p className="text-xs text-foreground-400 mt-1">{form.footer_description.length}/500</p>
              </div>
            </div>
          )}

          {/* Hero Tab */}
          {activeTab === 'hero' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                {sectionIcon('ri-image-line')}
                <div>
                  <h2 className="text-base font-heading font-semibold text-foreground-900">Hero Section</h2>
                  <p className="text-xs text-foreground-500">Main banner on the homepage</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-2">Hero Background Image URL</label>
                <input
                  type="text"
                  value={form.hero_image}
                  onChange={(e) => handleChange('hero_image', e.target.value)}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-colors font-mono"
                  placeholder="https://..."
                />
                {form.hero_image && (
                  <div className="mt-3 rounded-lg overflow-hidden border border-background-200 aspect-video">
                    <img
                      src={form.hero_image}
                      alt="Hero preview"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="800" height="450" fill="%23f1f5f9"><rect width="800" height="450"/><text x="400" y="225" text-anchor="middle" dy=".3em" fill="%2394a3b8" font-family="sans-serif" font-size="16">Image preview unavailable</text></svg>';
                      }}
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-2">Hero Title</label>
                <input
                  type="text"
                  value={form.hero_title}
                  onChange={(e) => handleChange('hero_title', e.target.value)}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-colors"
                  placeholder="Global Trade,"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-2">Hero Subtitle (highlighted)</label>
                <input
                  type="text"
                  value={form.hero_subtitle}
                  onChange={(e) => handleChange('hero_subtitle', e.target.value)}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-colors"
                  placeholder="Delivered."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-2">Hero Description</label>
                <textarea
                  value={form.hero_description}
                  onChange={(e) => handleChange('hero_description', e.target.value)}
                  maxLength={300}
                  rows={3}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-colors resize-none"
                  placeholder="Brief description for the hero section..."
                />
                <p className="text-xs text-foreground-400 mt-1">{form.hero_description.length}/300</p>
              </div>
            </div>
          )}

          {/* Contact Tab */}
          {activeTab === 'contact' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                {sectionIcon('ri-contacts-line')}
                <div>
                  <h2 className="text-base font-heading font-semibold text-foreground-900">Contact Information</h2>
                  <p className="text-xs text-foreground-500">Displayed on the contact page and footer</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-2">Email Address</label>
                <input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => handleChange('contact_email', e.target.value)}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-colors"
                  placeholder="info@bekaali.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-2">Phone Number</label>
                <input
                  type="text"
                  value={form.contact_phone}
                  onChange={(e) => handleChange('contact_phone', e.target.value)}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-colors"
                  placeholder="+254 700 000 000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-2">Address</label>
                <input
                  type="text"
                  value={form.contact_address}
                  onChange={(e) => handleChange('contact_address', e.target.value)}
                  className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-colors"
                  placeholder="Nairobi, Kenya"
                />
              </div>
            </div>
          )}

          {/* Social Tab */}
          {activeTab === 'social' && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                {sectionIcon('ri-share-line')}
                <div>
                  <h2 className="text-base font-heading font-semibold text-foreground-900">Social Media Links</h2>
                  <p className="text-xs text-foreground-500">Links displayed in the website footer</p>
                </div>
              </div>

              {[
                { key: 'linkedin_url' as const, label: 'LinkedIn', icon: 'ri-linkedin-fill', placeholder: 'https://linkedin.com/company/...' },
                { key: 'twitter_url' as const, label: 'Twitter / X', icon: 'ri-twitter-x-fill', placeholder: 'https://twitter.com/...' },
                { key: 'facebook_url' as const, label: 'Facebook', icon: 'ri-facebook-fill', placeholder: 'https://facebook.com/...' },
                { key: 'youtube_url' as const, label: 'YouTube', icon: 'ri-youtube-fill', placeholder: 'https://youtube.com/@...' },
              ].map((social) => (
                <div key={social.key}>
                  <label className="block text-sm font-medium text-foreground-700 mb-2">
                    <span className="flex items-center gap-2">
                      <i className={social.icon} />
                      {social.label}
                    </span>
                  </label>
                  <input
                    type="text"
                    value={form[social.key]}
                    onChange={(e) => handleChange(social.key, e.target.value)}
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-colors font-mono"
                    placeholder={social.placeholder}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bottom save button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-background-50 border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <i className="ri-save-line" />
                Save All Changes
              </>
            )}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}